import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import firebase from 'firebase/app';
import dayjs from 'dayjs';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';

import DateRangePickerField from '../../components/DateRangePickerField';
import EnhancedTableHead from '../../components/EnhancedTableHead';
import SimpleTableToolbar from '../../components/SimpleTableToolbar';
import EnhancedTableRow from '../../components/EnhancedTableRow';
import { getComparator, stableSort } from '../../modules/sort';
import ExpandButton from '../../components/ExpandButton';
import SearchBox from '../../components/SearchBox';
import FabAdd from '../../components/FabAdd';
import ReturnFormView from './ReturnFormView';
import { useFirestoreDataAndMapping, useSupplierDataAndMapping } from '../../modules/uitls';
import ContextStore from '../../modules/context';

function ReturnFormDetail({ userMapping, returnForm, ...props }) {
  const r = { ...returnForm }
  const keys = returnForm.merchandises ? Object.keys(returnForm.merchandises) : []
  r.merchandises = keys.map(k => {
    return {
      ...r.merchandises[k],
      id: k,
      price: r.merchandises[k].items.reduce((acc, cur) => acc + (cur.unitPrice * cur.amount), 0)
    }
  })

  r.createdBy = { id: r.createdBy, name: userMapping[r.createdBy]?.displayName }
  return (
    <div style={{ padding: 15 }}>
      <ReturnFormView userMapping={userMapping} returnForm={r} {...props} />
    </div>
  )
}

ReturnFormDetail.propTypes = {
  returnForm: PropTypes.object.isRequired,
  userMapping: PropTypes.object.isRequired,
  merchandiseMapping: PropTypes.object.isRequired,
};

function ReturnFormList() {
  const { formatMessage } = useIntl()
  const { setBreadcrumbs, currentUser } = useContext(ContextStore)
  const location = useLocation()

  const [userMapping] = useFirestoreDataAndMapping('users')
  const [customerMapping] = useFirestoreDataAndMapping('customers')
  const [supplierMapping] = useSupplierDataAndMapping()
  const [merchandiseMapping] = useFirestoreDataAndMapping('merchandises')

  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('sn')
  const [currentFilter, setCurrentFilter] = useState(null)
  const [expand, setExpand] = useState(true);
  const [returnForms, setReturnForms] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().subtract(2, 'months').format('YYYY-MM') + '-01')
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))

  useEffect(() => {
    setBreadcrumbs([{
      text: formatMessage({ id: 'sideMenu.purchase.returnForm' })
    }])
    return () => {
    };
  }, [location.pathname]);

  useEffect(() => {
    if (returnForms.length) setReturnForms([])
    const onSnapshot = snapshot => {
      const returnForms = []
      snapshot.forEach(doc => {
        returnForms.push({ ...doc.data(), id: doc.id })
      });
      setReturnForms(returnForms)
    }
    const unsubscribe = firebase.firestore().collection('returnForms').where('date', '>=', startDate).where('date', '<=', endDate).onSnapshot(onSnapshot, err => {})
    return () => unsubscribe()
  }, [startDate, endDate]);

  const returnFormsForTab = returnForms.filter(r =>
    currentUser.userRights.debugging || currentUser.returnFormSource.includes(r.source)
  );

  // const filteredReturnForms = currentFilter ? (returnFormsForTab.filter(s => s[currentFilter.name].toLowerCase().includes(currentFilter.text.toLowerCase()))) : returnFormsForTab
  const filteredReturnForms = currentFilter && currentFilter.text ? filterByText() : returnFormsForTab

  const headerCells = [
    { text: 'sn', sort: 'sn' },
    { text: 'sourceName', sort: 'sourceName' },
    { text: 'supplierName', sort: 'supplierName' },
    { text: 'createdBy', sort: 'createdBy' },
    { text: 'date', sort: 'date' },
  ].map(c => {c.text = formatMessage({ id: `returnForm.table.header.${c.text}` });return c})

  const rowCells = [
    { field: 'sn' },
    { field: 'sourceName' },
    { field: 'supplierName' },
    { field: 'createdBy' },
    { field: 'date' },
  ]

  function filterByText() {
    if (currentFilter.name === 'sourceName') {
      return returnFormsForTab.filter(s => customerMapping[s.source]?.nickname.toLowerCase().includes(currentFilter.text.toLowerCase()))
    } else if (currentFilter.name === 'createdBy') {
      return returnFormsForTab.filter(s => userMapping[s.createdBy]?.displayName.toLowerCase().includes(currentFilter.text.toLowerCase()))
    } else {
      return returnFormsForTab.filter(s => s[currentFilter.name].toLowerCase().includes(currentFilter.text.toLowerCase()))
    }
  }

  const formatData = (returnForm) => {
    const newData = { ...returnForm }
    newData.sourceName = customerMapping[newData.source]?.nickname
    newData.supplierName = supplierMapping[newData.supplier]?.nickname
    newData.createdBy = userMapping[newData.createdBy]?.displayName
    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filterItems = [
    { name: 'sn' },
    { name: 'sourceName', type: 'customer' },
    { name: 'supplierName' },
    { name: 'createdBy' },
    { name: 'note' },
  ].map(i => {i.text = formatMessage({ id: `returnForm.table.detail.${i.name}` });return i})

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  return (
    <div style={{ padding: '24px 24px 60px 24px' }}>
      {currentUser.userRights['returnForm-create'] === true && <FabAdd to="/purchase/returnForm/edit/new" />}
      <DateRangePickerField
        startDate={startDate}
        endDate={endDate}
        onStartDateChanged={setStartDate}
        onEndDateChanged={setEndDate}
      />
      <SimpleTableToolbar
        title="returnForm.table.title"
        buttons={<ExpandButton open={expand} onExpandChange={setExpand} />}
        toolbox={<SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} />}
      />
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: '650px' }} aria-label="collapsible table">
            <EnhancedTableHead
              headerCells={headerCells}
              order={order}
              orderBy={orderBy}
              expandable
              onRequestSort={handleRequestSort}
              rowCount={filteredReturnForms.length}
            />
            <TableBody>
              {stableSort(filteredReturnForms.map(r => formatData(r)), getComparator(order, orderBy)).map(returnForm => (
                <EnhancedTableRow
                  key={returnForm.id}
                  rowCells={rowCells}
                  cellData={returnForm}
                  expandable
                  expandContent={<ReturnFormDetail userMapping={userMapping} merchandiseMapping={merchandiseMapping} returnForm={returnForm} />}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </div>
  );
}

export default ReturnFormList;
